/* @ngInject */

export default (
  $q,
  $rootScope,
  $scope,
  $timeout,
  webpage,
  authenticationService,
  organizationService,
  navigationService,
  accountsService,
  partnersService,
  workflowsService,
  changesService,
  eventService,
  uiService,
  OrganizationFeatures,
  ACCOUNT,
  PARTNER
) => {
  /* @ngInject */

  $scope.Model = {
    availableAccounts: [],
    availablePartners: [],
    availableWorkflows: [],
    partnersList: null,
    isReady: false
  };

  /*
   * Initialize controller and start loading the required data
   * --------------------------------------------------------------------------------
   */
  webpage.pageTitle('Loading, please wait');
  if ($rootScope.CurrentAccount.isAdministrator) {
    $q.all([
      organizationService.readOrganization({ id: $rootScope.CurrentOrganization.id }),
      accountsService.getAvailable({ isActive: true, fields: ACCOUNT.FIELDS.LIST }),
      partnersService.getAvailable({ isActive: true, fields: PARTNER.FIELDS.LIST }),
      workflowsService.getAvailable({ isActive: true })
    ])
      .then((data) => {
        /* eslint-disable no-param-reassign */
        // Overwrite $rootScope values in case they've reverted to values within Token
        $rootScope.CurrentOrganization.features = OrganizationFeatures.build(data[0].features);
        $rootScope.CurrentOrganization.partnersAttributes = data[0].partnersAttributes;
        /* eslint-enable no-param-reassign */
        // Set Model values
        $scope.Model.availableAccounts = data[1];
        $scope.Model.availablePartners = data[2];
        $scope.Model.availableWorkflows = data[3];
      })
      .then(() => {
        // Declare Model as Ready and start rendering the page
        webpage.pageTitle('Partner Portal : Settings');
        $scope.Model.isReady = true;
      });
  }

  /*
   * Events available in Controller Scope
   * --------------------------------------------------------------------------------
   */
  $scope.$on('partnerAdd', (e, $event) => {
    eventService.accountAdd({ event: $event, isPartner: true });
  });
  $scope.$on('partnerDelete', ($event) => {
    const selectedPartners = _.without(
      $scope.Model.partnersList.getSelected(),
      _.find($scope.Model.partnersList.getSelected(), { isOwner: true })
    );
    const otherPartners = _.difference($scope.Model.availablePartners, selectedPartners);
    const currentAccount = $rootScope.CurrentAccount;
    currentAccount.isOwner = authenticationService.isOwner(currentAccount.id);
    eventService.accountDelete({
      event: $event,
      accounts: otherPartners,
      selectedAccounts: selectedPartners,
      isPartner: true
    });
  });

  /*
   * Methods available in Controller Scope
   * --------------------------------------------------------------------------------
   */
  $scope.toggleChanges = (group, field) => {
    changesService.change({ group, field });
  };
  $scope.editPartnerAddedContent = () => {
    eventService.dialogTextEdit({
      id: 'added',
      title: 'Edit E-Mail partner added content',
      content:
        $rootScope.CurrentOrganization.features.configPartnerPortal.notifications.partnerAdded
          .emailContent,
      mergeProperties: null,
      plainText: true
    });
  };
  $scope.editDealSharedContent = () => {
    eventService.dialogTextEdit({
      id: 'shared',
      title: 'Edit E-Mail shared deal content',
      content:
        $rootScope.CurrentOrganization.features.configPartnerPortal.notifications.dealShared
          .emailContent,
      mergeProperties: null,
      plainText: true
    });
  };
  $scope.textEdited = (_params) => {
    switch (_params.id) {
      case 'added':
        $rootScope.CurrentOrganization.features.configPartnerPortal.notifications.partnerAdded.emailContent =
          _params.content;
        $scope.toggleChanges('features', 'partnerAddedEmail');
        break;
      case 'shared':
        $rootScope.CurrentOrganization.features.configPartnerPortal.notifications.dealShared.emailContent =
          _params.content;
        $scope.toggleChanges('features', 'dealSharedEmail');
        break;
      default:
        break;
    }
  };
  $scope.partnerAssigned = (currentPartner, currentAccounts, accounts) => {
    if ($rootScope.CurrentOrganization.partnersAttributes[currentPartner.id]) {
      $rootScope.CurrentOrganization.partnersAttributes[currentPartner.id].idOwners = _.map(
        accounts,
        'id'
      );
    } else {
      $rootScope.CurrentOrganization.partnersAttributes[currentPartner.id] = {
        idOwners: _.map(accounts, 'id')
      };
    }
    $scope.toggleChanges('partnersAttributes', 'idOwners');
    $scope.saveChanges();
  };
  $scope.partnerAdded = (account) => {
    const exists = _.some($scope.Model.partnersList.getData(), (a) => a.id === account.id);
    if (!exists && account.id !== $rootScope.CurrentAccount.id) {
      partnersService.clearCache();
      $scope.Model.partnersList.setDataAppend(account);
      navigationService.organizationChange({
        idOrganization: $rootScope.CurrentOrganization.id
      });
      uiService.toast({
        text: {
          p1: 'Partner',
          p2: account.getName(),
          p3: 'has been added'
        }
      });
    } else {
      uiService.toast({
        text: {
          p1: 'Partner with E-Mail',
          p2: account.getEmail(),
          p3: 'already exists'
        },
        warn: true
      });
    }
  };
  $scope.partnerDeleted = (account) => {
    partnersService.clearCache();
    $scope.Model.partnersList.removeData(account);
    navigationService.organizationChange({
      idOrganization: $rootScope.CurrentOrganization.id
    });
  };
  $scope.changePartnersAttributes = (partnersAttributes) => {
    $rootScope.CurrentOrganization.partnersAttributes = partnersAttributes;
    $scope.toggleChanges('partnersAttributes', 'idPartner');
  };
  $scope.listChange = (list, data, isSelected) => {
    $scope.$parent.Model.childStateHasSelection = isSelected
      ? isSelected
      : list.isSelectedAny();
  };
  $scope.listReady = (list) => {
    $scope.Model.partnersList = list;
  };

  /*
   * Register and configure Changes service
   * --------------------------------------------------------------------------------
   */
  function saveChanges() {
    const organizationData = { id: $rootScope.CurrentOrganization.id, features: {} };
    if (changesService.hasChange({ group: 'features' })) {
      organizationData.features.configPartnerPortal =
        $rootScope.CurrentOrganization.features.configPartnerPortal;
    }
    if (changesService.hasChange({ group: 'partnersAttributes' })) {
      organizationData.partnersAttributes = $rootScope.CurrentOrganization.partnersAttributes;
    }
    return organizationService.updateOrganization(organizationData).then((result) => {
      if (result.status) {
        if (changesService.hasChange({ group: 'partnersAttributes' })) {
          navigationService.organizationChange({
            idOrganization: $rootScope.CurrentOrganization.id
          });
        }
        changesService.resetChanges();
      } else {
        uiService.toast({
          text: {
            p1: 'Failed to',
            p2: 'save',
            p3: 'your changes'
          },
          warn: true
        });
      }
      return { status: !!result.status };
    });
  }
  changesService.init({ useFinishOnInterval: true });
  changesService.onFinish(saveChanges);
};
